import { useRef, useState } from "react";
import { Upload, X, ImageIcon } from "lucide-react";

// Client-side image uploader — reads a file, downscales to a max side, converts
// to a base64 data URL and hands it back via onChange. Small enough to live on
// the program/organisation doc directly (Mongo 16MB limit).
// `aspect` controls the preview tile shape: "square" (default) or "strip" (3:1).
export function LogoUpload({ value, onChange, label = "Logo", size = 96, help, testId, aspect = "square", maxPx = 512 }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);

  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    setBusy(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        const url = canvas.toDataURL("image/png", 0.92);
        onChange(url);
        setBusy(false);
      };
      img.onerror = () => setBusy(false);
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const clear = (e) => { e.stopPropagation(); onChange(""); };

  const isStrip = aspect === "strip";
  const tileW = isStrip ? size * 3 : size;
  const tileH = isStrip ? Math.round(size * 0.8) : size;

  return (
    <div>
      <div className="text-[13px] font-medium text-[#0B0D10]/85 mb-2">{label}</div>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        data-testid={testId || "logo-upload"}
        className="w-full flex items-center gap-4 p-3 rounded-xl border-2 border-dashed border-[#E4E0D6] hover:border-[#0B0D10]/40 bg-white text-left transition-colors group"
      >
        <div
          style={{ width: tileW, height: tileH }}
          className={`shrink-0 ${isStrip ? "rounded-lg" : "rounded-2xl"} bg-[#F4F1E9] grid place-items-center overflow-hidden border border-[#E4E0D6] relative`}
        >
          {value ? (
            <>
              <img src={value} alt="" className="w-full h-full object-cover" />
              <span onClick={clear} className="absolute top-1 right-1 h-5 w-5 rounded-full bg-[#0B0D10]/70 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                <X size={10} strokeWidth={3} />
              </span>
            </>
          ) : (
            <ImageIcon size={isStrip ? 22 : 26} className="text-[#3a3d44]/40" strokeWidth={1.6} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-[13.5px] font-semibold text-[#0B0D10]">
            <Upload size={14} strokeWidth={2.2} />
            {value ? "Replace image" : busy ? "Reading…" : "Upload image"}
          </div>
          <div className="text-[12.5px] text-[#3a3d44]/60 mt-1 leading-relaxed">
            {help || "PNG or JPG. Square looks best. We resize automatically."}
          </div>
        </div>
      </button>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
    </div>
  );
}
